import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Fade,
  IconButton,
  Slide,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  Phone,
  PhoneDisabled,
  CallReceived,
  Close,
  Dialpad as DialpadIcon,
  Pause,
  PlayArrow,
  GroupAdd,
} from "@mui/icons-material";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTelephony } from "../../../contexts/TelephonyContext";
import { getSession } from "../../../auth";
import api from "../../../api";
import ContactsPanel from "../ContactsPanel";
import "./styles.css";
import { formatNumber, getCrmData, isStr, checkTelephonyTab } from "../Utils";
import TransferPanel from "../TransferPanel";
import ContactsAutocomplete from "../ContactsAutocomplete"
import MotivationPanel from "../MotivationPanel";
import ConferencePanel from "../ConferencePanel";
import ErrorBoundary from "../../ErrorBoundary";
import { useIncomingCallData } from "../hooks/useIncomingCallData";
import IncomingCallContent from "../IncomingCallContent";

const IncomingDialog = ({
  open,
  onClose,
  incomingData,
  answerCall,
  endCall,
  setIncomingData,
  setShowIncomingDialog,
  ctiData,
  setCtiData,
  uniqueid,
}) => {
  // Use custom hook for CRM data fetching and display logic
  const { crmData, loading, error, getDisplayName, getInitials } = useIncomingCallData(incomingData, uniqueid);

  const handleAnswer = () => {
    try {
      answerCall(incomingData?.number || null);
      setShowIncomingDialog(false);
    } catch (err) {
      console.error('Error answering call:', err);
      alert('Erro ao atender chamada. Por favor, tente novamente.');
      // Keep dialog open for retry
    }
  };

  const handleDecline = () => {
    try {
      endCall();
      setIncomingData(null);
      setCtiData(null);
      setShowIncomingDialog(false);
    } catch (err) {
      console.error('Error declining call:', err);
      // Force close dialog even on error
      setShowIncomingDialog(false);
    }
  };

  // Purple color scheme for telephone tab dialog
  const colorScheme = {
    primary: '#667eea',
    secondary: '#3766be',
    text: 'white',
    chipBg: 'rgba(255, 255, 255, 0.2)',
    chipText: 'white'
  };

  return (
    <Dialog
      id="newcall-dialog"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          background: "linear-gradient(135deg, #667eea 0%, #3766beff 100%)",
          color: "white",
          minHeight: 400,
        },
      }}
      TransitionComponent={Fade}
      transitionDuration={300}
    >

      <DialogTitle
        sx={{
          textAlign: "center",
          pb: 0,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            opacity: 0.7,
            "&:hover": { opacity: 1 },
          }}
        >
          <Close />
        </IconButton>
        
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
          <CallReceived sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Chamada Recebida
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ textAlign: "center", pb: 2 }}>
        <IncomingCallContent
          crmData={crmData}
          incomingData={incomingData}
          ctiData={ctiData}
          loading={loading}
          error={error}
          colorScheme={colorScheme}
          showError={true}
          showAvatar={true}
          getDisplayName={getDisplayName}
          getInitials={getInitials}
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: "space-around" }}>
        <Button
          variant="contained"
          onClick={handleDecline}
          disabled={loading}
          sx={{
            backgroundColor: "#f44336",
            color: "white",
            borderRadius: "50px",
            minWidth: 120,
            height: 56,
            fontSize: 16,
            fontWeight: "bold",
            boxShadow: "0 4px 15px rgba(244, 67, 54, 0.4)",
            "&:hover": {
              backgroundColor: "#d32f2f",
              boxShadow: "0 6px 20px rgba(244, 67, 54, 0.6)",
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            transition: "all 0.2s ease-in-out",
          }}
          startIcon={<PhoneDisabled />}
        >
          Recusar
        </Button>

        <Button
          variant="contained"
          onClick={() => {
            handleAnswer();
          }}
          disabled={loading}
          sx={{
            backgroundColor: "#4caf50",
            color: "white",
            borderRadius: "50px",
            minWidth: 120,
            height: 56,
            fontSize: 16,
            fontWeight: "bold",
            boxShadow: "0 4px 15px rgba(76, 175, 80, 0.4)",
            "&:hover": {
              backgroundColor: "#388e3c",
              boxShadow: "0 6px 20px rgba(76, 175, 80, 0.6)",
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            transition: "all 0.2s ease-in-out",
          }}
          startIcon={<Phone />}
        >
          Atender
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialpadComponent = ({ open, onClose, onDigitPress, onBackspace, displayValue }) => {
  const dialpadButtons = [
    { digit: '1', letters: '' },
    { digit: '2', letters: 'ABC' },
    { digit: '3', letters: 'DEF' },
    { digit: '4', letters: 'GHI' },
    { digit: '5', letters: 'JKL' },
    { digit: '6', letters: 'MNO' },
    { digit: '7', letters: 'PQRS' },
    { digit: '8', letters: 'TUV' },
    { digit: '9', letters: 'WXYZ' },
    { digit: '*', letters: '' },
    { digit: '0', letters: '+' },
    { digit: '#', letters: '' }
  ];

  const handleDigitClick = (digit) => {
    onDigitPress(digit);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up" }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #3766beff 100%)",
          color: "white",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
          <DialpadIcon sx={{ fontSize: 28 }} />
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            opacity: 0.7,
            "&:hover": { opacity: 1 },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {/* Display Screen */}
        <Paper
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            p: 2,
            mb: 3,
            borderRadius: 2,
            textAlign: "center",
            minHeight: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "white",
              fontFamily: "monospace",
              letterSpacing: 2,
              minHeight: "1em",
              wordBreak: "break-all",
            }}
          >
            {displayValue || ""}
          </Typography>
        </Paper>

        {/* Dialpad Grid */}
        <Grid container spacing={1.5}>
          {dialpadButtons.map((button, index) => (
            <Grid item xs={4} key={button.digit}>
              <Button
                variant="contained"
                onClick={() => handleDigitClick(button.digit)}
                sx={{
                  width: "100%",
                  height: 70,
                  borderRadius: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  color: "white",
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                    transform: "scale(1.02)",
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                  transition: "all 0.15s ease",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold", lineHeight: 1 }}>
                  {button.digit}
                </Typography>
                {button.letters && (
                  <Typography variant="caption" sx={{ fontSize: "0.65rem", opacity: 0.8, lineHeight: 1 }}>
                    {button.letters}
                  </Typography>
                )}
              </Button>
            </Grid>
          ))}
        </Grid>

        {/* Action Buttons */}
        {/* <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="contained"
            onClick={onBackspace}
            disabled={!displayValue}
            sx={{
              minWidth: 60,
              height: 50,
              borderRadius: 2,
              backgroundColor: "rgba(244, 67, 54, 0.8)",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(244, 67, 54, 1)",
              },
              "&:disabled": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            <Backspace />
          </Button>
        </Box> */}
      </DialogContent>
    </Dialog>
  );
};

const ConferenceLoadingModal = ({ open }) => {
  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={true}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #3766beff 100%)",
          color: "white",
        },
      }}
      onClose={(event, reason) => {
        // Prevent closing by clicking outside or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
    >
      <DialogContent sx={{ textAlign: "center", py: 6 }}>
        <Box sx={{ mb: 3 }}>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{ color: "white" }}
          />
        </Box>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          Aguarde...
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Conectando participantes e transferindo chamada
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

const IdlePanel = ({
  initiateCall,
  callStatus,
  sipSignal,
  clearSipSignal,
  endCall,
  answerCall,
  ctiData,
  setCtiData,
  incomingData,
  setIncomingData,
  pendingNumber,
  dialNumberFromHistory,
  setDialNumberFromHistory,
  uniqueid,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [showIncomingDialog, setShowIncomingDialog] = useState(false);
  const [showContactsPanel, setShowContactsPanel] = useState(false);
  const location = useLocation();
  const dialBtnRef = useRef(null);
  const dismissedIncomingCallRef = useRef(null);
  const incomingCallKey = String(
    uniqueid ||
      incomingData?.uniqueid ||
      incomingData?.id ||
      callStatus?.timestamp ||
      incomingData?.number ||
      'current'
  );

  const closeIncomingDialog = () => {
    dismissedIncomingCallRef.current = incomingCallKey;
    setShowIncomingDialog(false);
  };

  // If the user leaves the telephony tab while the call is still ringing, the
  // widget becomes responsible for the alert. Clear the local dismissal so the
  // modal can be shown again if the user returns to the telephony tab during
  // the same ringing call.
  useEffect(() => {
    if (callStatus.status === "ringing" && !checkTelephonyTab()) {
      dismissedIncomingCallRef.current = null;
      if (showIncomingDialog) {
        setShowIncomingDialog(false);
      }
    }
  }, [location.pathname, location.search, callStatus.status, showIncomingDialog]);

  // garantees autocomplete/dialog is closed and DOM is stabilized
  const focusDialButton = () => {
    requestAnimationFrame(() => dialBtnRef.current?.focus());
  };

  // Set searchValue when pendingNumber is provided
  useEffect(() => {
    if (pendingNumber) {
      setSearchValue(pendingNumber);
    }
  }, [pendingNumber]);

  useEffect(() => {
    console.log("IdlePanel callStatus changed:", callStatus);
    if (callStatus.status === "dialing") {
      if (dialNumberFromHistory) {
        setSearchValue(dialNumberFromHistory);
        
      }
    } else {
      setDialNumberFromHistory(null);
    }
  }, [callStatus, dialNumberFromHistory, setDialNumberFromHistory]);

  // Function to handle contact selection
  const handleContactSelect = (event, contact) => {
    const num = contact.phone || contact.number || "";
    setSearchValue(num);
    setShowContactsPanel(false);
    focusDialButton();
  };

  // Function to handle dial action
  const handleDialAction = (overrideNumber) => {
    const raw = overrideNumber ?? searchValue;
    if (callStatus.status === "connected" || callStatus.status === "dialing") {
      endCall();
      return;
    }
    if (isStr(raw) && raw.trim()) {
      initiateCall(raw.trim());
    }
  };


  // Effect to listen for telephony signals from MainPanel
  useEffect(() => {
    if (sipSignal) {

      switch (sipSignal.action) {
        case "incoming":
          dismissedIncomingCallRef.current = null;
          setIncomingData({ number: sipSignal.number, info: sipSignal.info });
          setShowIncomingDialog(true);
          break;

        case "cti":
          setCtiData(sipSignal);
          break;
        case "callEnded":
          dismissedIncomingCallRef.current = null;
          setShowIncomingDialog(false);
          setIncomingData(null);
          break;

        default:
          console.log("Unknown sip signal:", sipSignal.action);
      }

      // Clear the signal after processing
      clearSipSignal();
    }
  }, [sipSignal, clearSipSignal]);

  // When an incoming call starts outside the telephony tab, it is delivered
  // through widgetSignal and stored in the shared telephony context. If the
  // user navigates to the telephony tab while the call is still ringing, the
  // local sipSignal has already been consumed by the widget, so open the
  // incoming dialog from the shared call state.
  useEffect(() => {
    if (callStatus.status === "ringing" && incomingData && !showIncomingDialog) {
      if (dismissedIncomingCallRef.current !== incomingCallKey) {
        setShowIncomingDialog(true);
      }
      return;
    }

    if (callStatus.status !== "ringing") {
      dismissedIncomingCallRef.current = null;
      if (showIncomingDialog) {
        setShowIncomingDialog(false);
      }
    }
  }, [callStatus.status, incomingData, incomingCallKey, showIncomingDialog]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "flex-start",
        paddingTop: "20vh",
      }}
    >
      <Grid container spacing={2} sx={{ width: "100%" }}>
        <Grid item xs={12}>
          <Box sx={{ width: "80%", margin: "0 auto" }}>
            <ContactsAutocomplete
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              handleEnterAction={handleDialAction}
              onAfterSelect={focusDialButton}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              marginTop: "10px",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowContactsPanel(true)}
            >
              Selecionar Contato
            </Button>
            <Button
              ref={dialBtnRef}
              variant="contained"
              color="primary"
              disableRipple
              disableFocusRipple
              sx={{
                "&.Mui-focusVisible": { boxShadow: "none" },
                "&:focus": { outline: "none" },
                "&:focus-visible": { outline: "none" },
              }}
              onClick={() => handleDialAction()}
            >
              {callStatus.status !== "dialing"
                ? callStatus.status === "connected"
                  ? "Desligar"
                  : "Discar"
                : "Desligar"}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {showIncomingDialog && (
        <ErrorBoundary
          componentName="IncomingDialog"
          title="Erro na Chamada Recebida"
          message="Erro ao exibir informações da chamada recebida."
          showReload={true}
          logToBackend={true}
        >
          <IncomingDialog
            open={showIncomingDialog}
            onClose={closeIncomingDialog}
            incomingData={incomingData}
            answerCall={answerCall}
            endCall={endCall}
            ctiData={ctiData}
            setCtiData={setCtiData}
            setIncomingData={setIncomingData}
            setShowIncomingDialog={setShowIncomingDialog}
            uniqueid={uniqueid}
          />
        </ErrorBoundary>
      )}

      {showContactsPanel && (
        <ErrorBoundary
          componentName="ContactsPanel"
          title="Erro nos Contatos"
          message="Erro ao carregar a lista de contatos."
          showReset={true}
          onReset={() => setShowContactsPanel(false)}
          logToBackend={true}
        >
          <ContactsPanel
            open={showContactsPanel}
            onClose={() => setShowContactsPanel(false)}
            handleContactSelected={handleContactSelect}
          />
        </ErrorBoundary>
      )}
    </Box>
  );
};

const ConnectedPanel = ({ dialNumber, endCall, sipSignal, incomingData, ctiData, sendDTMF , hold, unhold, callReasons, uniqueid, callDirection, callStatus }) => {
  const { initiateCall, toggleMute, muted } = useTelephony();
  const [callDuration, setCallDuration] = useState(0);
  const [showTransferPanel, setShowTransferPanel] = useState(false);
  const [showConferencePanel, setShowConferencePanel] = useState(false);
  const [showDialpad, setShowDialpad] = useState(false);
  const [showMotivationPanel, setShowMotivationPanel] = useState(false);
  const [dialpadValue, setDialpadValue] = useState("");
  const [isOnHold, setIsOnHold] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [lastCallId, setLastCallId] = useState(null);
  const [isConferencing, setIsConferencing] = useState(false);
  const [attendedTransferActive, setAttendedTransferActive] = useState(false);
  const transferTimeoutRef = useRef(null);
  const isConferenceCall =
    typeof dialNumber === "string" && dialNumber.startsWith("*32");
  const [isConferencingStarted, setIsConferencingStarted] = useState(() => isConferenceCall);

  useEffect(() => {
    if (isConferenceCall) setIsConferencingStarted(true);
  }, [isConferenceCall]);

  useEffect(() => {
    return () => {
      if (transferTimeoutRef.current) {
        clearTimeout(transferTimeoutRef.current);
      }
    };
  }, []);

  // Timer effect to track call duration based on callStatus.timestamp
  useEffect(() => {
    const calculateDuration = () => {
      if (callStatus?.status === 'connected' && callStatus?.timestamp) {
        const elapsedSeconds = Math.floor((Date.now() - callStatus.timestamp) / 1000);
        setCallDuration(elapsedSeconds);
      } else {
        setCallDuration(0);
      }
    };

    // Calculate immediately
    calculateDuration();

    // Update every second
    const timer = setInterval(() => {
      calculateDuration();
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [callStatus?.timestamp, callStatus?.status]);

  // Format duration to MM:SS format
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Dialpad functions
  const handleDialpadDigit = (digit) => {
    setDialpadValue(prev => prev + digit);
    // Here you can add DTMF tone sending logic if needed
    console.log("DTMF digit pressed:", digit);
    sendDTMF(digit);
  };

  const handleDialpadBackspace = () => {
    setDialpadValue(prev => prev.slice(0, -1));
  };

  const handleDialpadClose = () => {
    setShowDialpad(false);
    setDialpadValue("");
  };

  // Hold/Unhold functions
  const handleHoldToggle = async () => {
    try {
      if (isOnHold) {
        await unhold();
        setIsOnHold(false);
        console.log("Call resumed from hold");
      } else {
        await hold();
        setIsOnHold(true);
        console.log("Call put on hold");
      }
    } catch (error) {
      console.error("Error toggling hold state:", error);
    }
  };
  
  const handleAttendedTransferStarted = () => {    
    setAttendedTransferActive(true);    
    
    if (transferTimeoutRef.current) {
      clearTimeout(transferTimeoutRef.current);
      transferTimeoutRef.current = null;
    }

    transferTimeoutRef.current = setTimeout(() => {
      setAttendedTransferActive(false);
      transferTimeoutRef.current = null;
      console.log("ATXFER timeout expired, resetting attended transfer state");
    }, 60000);
  };

  const handleCancelAttendedTransfer = () => {
    try {
      sendDTMF('*');
    } catch (error) {
      console.error("Error sending DTMF to cancel attended transfer:", error);
    } finally {      
      if (transferTimeoutRef.current) {
        clearTimeout(transferTimeoutRef.current);
        transferTimeoutRef.current = null;
      }      
      setAttendedTransferActive(false);
    }
  };

  const conferenceLocked = isConferencing || isConferencingStarted || isConferenceCall;
  
  return (
    <>
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "flex-start",
        paddingTop: "20vh",
      }}
    >
      <Grid container spacing={2} sx={{ width: "100%", textAlign: "center" }}>
        <Grid item xs={12}>
          <Typography variant="h5">
            Chamada em andamento com {dialNumber}
          </Typography>
          <Typography variant="h6" sx={{ color: "primary.main", fontWeight: "bold", mt: 1 }}>
            Duração: {formatDuration(callDuration)}
          </Typography>
          
          {/* Hold Status Indicator */}
          {isOnHold && (
            <Chip
              icon={<Pause />}
              label="Chamada em Espera"
              color="warning"
              sx={{
                mt: 1,
                fontWeight: "bold",
                animation: "pulse 1.5s infinite",
              }}
            />
          )}
          
          {ctiData && (
            <Typography variant="body1" sx={{ mt: 1 }}>
              {ctiData.queue && `Fila: ${ctiData.queue} `}
              {ctiData && ctiData.cti && `CTI: ${ctiData.cti} `}
              {ctiData && ctiData.clientCode && ctiData?.clientCode?.trim() && `Cód. Cliente: ${ctiData.clientCode}`}
            </Typography>
          )}
        </Grid>
        {/* Row 1: Desligar and Transferir */}
        <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-end", pr: 1 }}>
          <Button
            variant="contained"
            color="error"
            onClick={endCall}
            sx={{ padding: "10px 40px", fontSize: "18px", width: "100%", maxWidth: "280px" }}
          >
            Desligar
          </Button>
        </Grid>
        <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-start", pl: 1 }}>
          {attendedTransferActive ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCancelAttendedTransfer}
              sx={{ padding: "10px 40px", fontSize: "18px", width: "100%", maxWidth: "280px" }}
            >
              Cancelar transferência
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setShowTransferPanel(true)}
              sx={{ padding: "10px 40px", fontSize: "18px", width: "100%", maxWidth: "280px" }}
            >
              Transferir
            </Button>
          )}
        </Grid>

        {/* Row 2: Conferência and Hold */}
        <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-end", pr: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowConferencePanel(true)}
            disabled={conferenceLocked}
            startIcon={<GroupAdd />}
            sx={{
              padding: "10px 40px",
              fontSize: "18px",
              width: "100%",
              maxWidth: "280px",
              backgroundColor: "#2196f3",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
              boxShadow: "0 4px 15px rgba(33, 150, 243, 0.4)",
            }}
          >
            Conferência
          </Button>
        </Grid>
        <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-start", pl: 1 }}>
          <Button
            variant="contained"
            color={isOnHold ? "success" : "warning"}
            onClick={handleHoldToggle}
            startIcon={isOnHold ? <PlayArrow /> : <Pause />}
            sx={{
              padding: "10px 40px",
              fontSize: "18px",
              width: "100%",
              maxWidth: "280px",
              backgroundColor: isOnHold ? "#4caf50" : "#ff9800",
              "&:hover": {
                backgroundColor: isOnHold ? "#388e3c" : "#f57c00",
              },
              boxShadow: isOnHold ? "0 4px 15px rgba(76, 175, 80, 0.4)" : "0 4px 15px rgba(255, 152, 0, 0.4)",
            }}
          >
            {isOnHold ? "Retomar" : "Hold"}
          </Button>
        </Grid>

        {/* Row 3: Motivar and Dialpad */}
        <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-end", pr: 1 }}>
          <Button
            variant="contained"
            color="info"
            sx={{ padding: "10px 40px", fontSize: "18px", width: "100%", maxWidth: "280px" }}
            onClick={() => setShowMotivationPanel(true)}
          >
            Motivar
          </Button>
        </Grid>
        <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-start", pl: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowDialpad(true)}
            sx={{ padding: "10px 40px", fontSize: "18px", width: "100%", maxWidth: "280px" }}
            startIcon={<DialpadIcon />}
          >
            Dialpad
          </Button>
        </Grid>
      </Grid>
    </Box>
    {showTransferPanel && (
      <ErrorBoundary
        componentName="TransferPanel"
        title="Erro na Transferência"
        message="Erro ao abrir painel de transferência."
        showReset={true}
        onReset={() => setShowTransferPanel(false)}
        logToBackend={true}
      >
        <TransferPanel
          open={showTransferPanel}
          onClose={() => setShowTransferPanel(false)}
          // handleContactSelected={handleContactSelect}
          onAttendedTransferStarted={handleAttendedTransferStarted}
        />
      </ErrorBoundary>
    )}

    {showConferencePanel && (
      <ErrorBoundary
        componentName="ConferencePanel"
        title="Erro na Conferência"
        message="Erro ao abrir painel de conferência."
        showReset={true}
        onReset={() => setShowConferencePanel(false)}
        logToBackend={true}
      >
        <ConferencePanel
          open={showConferencePanel}
          onClose={() => setShowConferencePanel(false)}
          currentCallee={dialNumber}
          onStartConference={async (participants) => {
            try {
              setIsConferencing(true);
              setShowConferencePanel(false);

              // Get user extension for conference room
              const user = getSession();
              const extension = user.profile.agent.preferred_interface.split("_")[1];
              // TODO: Get the *32 from the config table.
              const conferenceRoom = `*32${extension}`;
              const agentId = user.profile.agent_id;

              console.log("Starting conference:", {
                participants,
                conferenceRoom,
                extension,
                agentId
              });

              // 1. Initiate calls for all participants to conference room
              const response = await api.post(
                `/api/IV/agents/${agentId}/conference/`,
                {
                  participants: participants.map(p => p.number),
                  conference_room: conferenceRoom,
                }
              );

              console.log("Conference API response:", response.data);

              // Check for partial failures
              const failedParticipants = response.data.participants.filter(
                p => p.status === "failed"
              );

              if (failedParticipants.length > 0) {
                console.warn("Some participants failed:", failedParticipants);
              }
              setIsConferencingStarted(true);

              // 2. Store current mute state and mute call before transfer
              // const wasMuted = muted;
              // if (!muted) {
              //   toggleMute();
              // }

              // 3. Blind transfer current call to conference room
              sendDTMF(`*7${conferenceRoom}`);

              // 4. Wait for transfer to complete
              await new Promise(resolve => setTimeout(resolve, 10000));

              // 5. Initiate new call for user to join conference
              initiateCall(conferenceRoom);

              // 6. Wait for agent to reconnect to conference
              // Wait a bit longer to ensure the call connects (conservative approach)
              // The modal will stay visible until the call is fully established
              await new Promise(resolve => setTimeout(resolve, 5000));

              // 7. Restore original mute state
              // if (!wasMuted && muted) {
              //   toggleMute();
              // }

              // 8. Hide modal
              setIsConferencing(false);

              // 9. Show feedback
              if (failedParticipants.length > 0) {
                alert(
                  `Conferência iniciada!\nAtenção: ${failedParticipants.length} participante(s) falharam ao conectar.`
                );
              }

            } catch (error) {
              console.error("Conference error:", error);
              alert("Erro ao iniciar conferência. Tente novamente.");
              setIsConferencing(false);
              setIsConferencingStarted(false);
            }
          }}
        />
      </ErrorBoundary>
    )}

    {showDialpad && (
      <ErrorBoundary
        componentName="DialpadComponent"
        title="Erro no Teclado"
        message="Erro ao exibir o teclado numérico."
        showReset={true}
        onReset={() => setShowDialpad(false)}
        logToBackend={true}
      >
        <DialpadComponent
          open={showDialpad}
          onClose={handleDialpadClose}
          onDigitPress={handleDialpadDigit}
          onBackspace={handleDialpadBackspace}
          displayValue={dialpadValue}
        />
      </ErrorBoundary>
    )}

    {showMotivationPanel && (
      <ErrorBoundary
        componentName="MotivationPanel"
        title="Erro nas Motivações"
        message="Erro ao carregar painel de motivações."
        showReset={true}
        onReset={() => setShowMotivationPanel(false)}
        logToBackend={true}
      >
        <MotivationPanel
          callReasons={callReasons}
          open={showMotivationPanel}
          onClose={() => setShowMotivationPanel(false)}
          currentCallId={incomingData?.info?.uniqueid}
          saveReasons={async (reasons) => {
          const enterpriseId = getSession().profile.enterprise.id;
          const agentId = getSession().profile.agent_id;
          let payload = {};
          if (incomingData && incomingData.info) {
            payload = {
              uniqueid: incomingData.info.uniqueid,
              reasons: reasons.map(r => r.id),
              call_type: incomingData.info.callType,
            }
          } else if (uniqueid) {
            payload = {
              uniqueid: uniqueid,
              reasons: reasons.map(r => r.id),
              call_type: callDirection,
            }
          } else {
            console.error("No call unique ID available to save motivations.");
            setShowMotivationPanel(false);
            return;
          }
          const response = await api.post(
            `/api/enterprises/${enterpriseId}/agents/${agentId}/motivation/`,
            payload,
            { timeout: 15000 }
          );
          if (response.status >= 200 && response.status < 300) {
            console.log("Motivations saved successfully");
          } else {
            console.error("Error saving motivations:", response);
          }

          setShowMotivationPanel(false);

          // TODO: Save the selected motivations
        }}
        loadReasons={ (callId) => { return []; } }
        selectedReasons={selectedReasons}
        setSelectedReasons={setSelectedReasons}
        setLastCallId={setLastCallId}
        lastCallId={lastCallId}
      />
      </ErrorBoundary>
    )}

    <ConferenceLoadingModal open={isConferencing} />
    </>
  );
};

const MainPanel = ({
  dialNumberFromHistory,
  setDialNumberFromHistory,
}) => {
  const {
    initiateCall,
    callStatus,
    dialNumber,
    sipSignal,
    clearSipSignal,
    endCall,
    answerCall,
    sendDTMF,
    hold,
    unhold,
    incomingData,
    setIncomingData,
    uniqueid,
    callDirection,
    ctiData,
    setCtiData,
  } = useTelephony();

  const [callReasons, setCallReasons] = useState([]);
  const [pendingNumber, setPendingNumber] = useState(null);

  useEffect(async () => {
    const enterpriseId = getSession().profile.enterprise.id;
    const agentId = getSession().profile.agent_id;
    const response = await api.get(`/api/enterprises/${enterpriseId}/agents/${agentId}/motivations/`);
    if (response.status >= 200 && response.status < 300) {
      const motivation = response.data;
      setCallReasons(motivation);
    }
  }, []);

  // Check for pending outbound call from ChatWindow
  useEffect(() => {
    const pendingNumberFromStorage = localStorage.getItem('telephony:pendingOutboundCall');
    if (pendingNumberFromStorage) {
      // Clear the pending call immediately to avoid re-initiating
      localStorage.removeItem('telephony:pendingOutboundCall');
      
      // Set the number in state so IdlePanel can display it
      setPendingNumber(pendingNumberFromStorage);
      
      // Only initiate if we're not already in a call
      if (callStatus.status === 'idle') {
        initiateCall(pendingNumberFromStorage);
      }
    }
  }, [callStatus, initiateCall]);

  if (callStatus.status == "connected") {
    return (
      <ConnectedPanel
        dialNumber={dialNumber}
        endCall={endCall}
        sipSignal={sipSignal}
        incomingData={incomingData}
        ctiData={ctiData}
        sendDTMF={sendDTMF}
        hold={hold}
        unhold={unhold}
        callReasons={callReasons}
        uniqueid={uniqueid}
        callDirection={callDirection}
        callStatus={callStatus}
      />
    );
  } else {
    return (
      <IdlePanel
        initiateCall={initiateCall}
        callStatus={callStatus}
        sipSignal={sipSignal}
        clearSipSignal={clearSipSignal}
        endCall={endCall}
        answerCall={answerCall}
        ctiData={ctiData}
        setCtiData={setCtiData}
        incomingData={incomingData}
        setIncomingData={setIncomingData}
        pendingNumber={pendingNumber}
        dialNumberFromHistory={dialNumberFromHistory}
        setDialNumberFromHistory={setDialNumberFromHistory}
        uniqueid={uniqueid}
      />
    );
  }
};

export default MainPanel;
